/**
 * A device build's log in a dialog: GET .../log?from=<next> every 2 s while the build is not done, the new text appended
 * and the view kept at the bottom (unless the person scrolled up to read).
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'antd';
import { getDeviceBuildLog } from '../../services/DeviceBuildsService';

export const LOG_POLL_MS = 2000;

export default function DeviceBuildLog({ open, onClose, projectId, build }) {
  const [text, setText] = useState('');
  const [done, setDone] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState(null);
  const box = useRef(null);
  const follow = useRef(true);
  const buildId = build?.id;

  useEffect(() => {
    if (!open || !buildId) { return undefined; }
    let live = true;
    let timer = null;
    let from = 0;
    setText('');
    setDone(false);
    setTruncated(false);
    setError(null);
    follow.current = true;
    const read = async() => {
      try {
        const piece = await getDeviceBuildLog(projectId, buildId, from);
        if (!live) { return; }
        if (piece?.text) { setText((prev) => prev + piece.text); }
        if (piece?.truncated) { setTruncated(true); }
        from = Number.isFinite(piece?.next) ? piece.next : from;
        setError(null);
        if (piece?.done) { setDone(true); return; }
      } catch (err) {
        if (!live) { return; }
        setError(err?.message || 'The log could not be read.');
      }
      timer = setTimeout(read, LOG_POLL_MS);
    };
    read();
    return () => { live = false; clearTimeout(timer); };
  }, [open, projectId, buildId]);

  useEffect(() => {
    const node = box.current;
    if (node && follow.current) { node.scrollTop = node.scrollHeight; }
  }, [text]);

  const onScroll = () => {
    const node = box.current;
    if (node) { follow.current = node.scrollHeight - node.scrollTop - node.clientHeight < 24; }
  };

  return (
    <Modal destroyOnHidden footer={null} onCancel={onClose} open={open} title="Build log" width={860} zIndex={1200}>
      <div data-testid="devb-log">
        {truncated && <p className="devb-muted" data-testid="devb-log-truncated">The beginning of the log was cut: it was too long.</p>}
        <pre className="devb-log" data-testid="devb-log-text" onScroll={onScroll} ref={box}>{text || (done ? 'The log is empty.' : 'Waiting for the log…')}</pre>
        <p className="devb-muted" data-testid="devb-log-state">
          {error ? <span className="devb-bad">{error} Trying again…</span> : done ? 'The build has ended.' : 'Following the build…'}
        </p>
      </div>
    </Modal>
  );
}

DeviceBuildLog.propTypes = {
  build: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  projectId: PropTypes.string.isRequired,
};
